import type { CustomField } from '../types';

/**
 * Filters custom fields based on selected department and office
 * @param customFields - Array of custom fields from API
 * @param selectedDeptId - Currently selected department ID
 * @param selectedOfficeId - Currently selected office ID
 * @returns Filtered array of custom fields that should be displayed
 */
export const filterCustomFieldsBySelection = (
  customFields: CustomField[],
  selectedDeptId: number | null,
  selectedOfficeId: number[] | null
): CustomField[] => {
  if (!customFields || customFields.length === 0) {
    return [];
  }

  return customFields.filter((field) => {
    // Always show global fields (empty departments/offices arrays)
    const isGlobalField =
      field.departments.length === 0 && field.offices.length === 0;

    if (isGlobalField) {
      return true;
    }

    // For specific fields, check if department and office match
    let deptMatch = true;
    let officeMatch = true;

    // Check department restriction
    if (field.departments.length > 0) {
      deptMatch =
        selectedDeptId !== null &&
        field.departments.some((dept) => dept.id === selectedDeptId);
    }

    // Check office restriction
    if (field.offices.length > 0) {
      officeMatch =
        selectedOfficeId !== null &&
        field.offices.some((office) => selectedOfficeId?.includes(office.id));
    }

    // Field is shown only if both department and office match (when restricted)
    return deptMatch && officeMatch;
  });
};

/**
 * Categorizes custom fields into global and specific
 * @param customFields - Array of custom fields from API
 * @returns Object with global and specific field arrays
 */
export const categorizeCustomFields = (customFields: CustomField[]) => {
  const globalFields: CustomField[] = [];
  const specificFields: CustomField[] = [];

  customFields.forEach((field) => {
    const isGlobalField =
      field.departments.length === 0 && field.offices.length === 0;

    if (isGlobalField) {
      globalFields.push(field);
    } else {
      specificFields.push(field);
    }
  });

  return { globalFields, specificFields };
};

/**
 * Gets summary of which departments/offices have specific custom fields
 * @param customFields - Array of custom fields from API
 * @returns Summary object with department and office specific field counts
 */
export const getCustomFieldsSummary = (customFields: CustomField[]) => {
  const deptCounts: Record<number, number> = {};
  const officeCounts: Record<number, number> = {};

  customFields.forEach((field) => {
    // Count fields per department
    field.departments.forEach((dept) => {
      deptCounts[dept.id] = (deptCounts[dept.id] || 0) + 1;
    });

    // Count fields per office
    field.offices.forEach((office) => {
      officeCounts[office.id] = (officeCounts[office.id] || 0) + 1;
    });
  });

  return {
    departmentFieldCounts: deptCounts,
    officeFieldCounts: officeCounts,
    totalGlobalFields: customFields.filter(
      (f) => f.departments.length === 0 && f.offices.length === 0
    ).length,
    totalSpecificFields: customFields.filter(
      (f) => f.departments.length > 0 || f.offices.length > 0
    ).length,
  };
};
